import { Popover, PopoverPanel } from '@headlessui/react';
import classNames from 'classnames';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { client } from '~/client';
import { OTHER_ASSET_TYPE } from '~/constants/template';
import { graphql } from '~/gql';
import { Option } from '~/types';
import { Combolist } from './Combolist';
import { SearchInput } from './FindAddContact/SearchInput';
import { PopoverStateChangedEffect } from './PopoverStateChangedEffect';
import { HoverTooltip } from './ui/HoverTooltip';
import { Label } from './ui/Label';
import { MenuTransition } from './ui/Transition/MenuTransition';

export { buildAssetTypeOptions } from './assetTypeOptions';

interface CombolistItem {
  id: string;
  name: string;
  $group?: string;
}

type Props = {
  value: string[];
  onChange: (value: string[]) => void;
  options: Option[];
  error?: string;
};

const AssetTypesDocument = graphql(`
  query AssetTypes {
    assetTypes
  }
`);

export function useAssetTypes() {
  const [assetTypes, setAssetTypes] = useState<string[]>([]);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      try {
        const result = await client
          .query(AssetTypesDocument, {}, { requestPolicy: 'cache-and-network' })
          .toPromise();

        if (isMounted) {
          setAssetTypes(result.data?.assetTypes ?? []);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Failed to load asset types:', error);
        }
      }
    }

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  return [assetTypes];
}
const DISPLAY_LIMIT = 3;

/**
 * Helper function to wrap content with a tooltip if it's the OTHER_ASSET_TYPE.
 * This centralizes the tooltip logic to avoid duplication.
 * Accepts a key parameter to preserve React keys when wrapping elements in lists.
 */
const wrapWithOtherAssetTypeTooltip = (
  assetTypeId: string,
  content: React.ReactNode,
  placement: 'top' | 'right' = 'top',
  t: TFunction,
  key?: string | number
): React.ReactNode => {
  if (assetTypeId === OTHER_ASSET_TYPE) {
    const tooltipContent = (
      <HoverTooltip
        content={t(
          'jobTemplate.otherAssetTypeTooltip',
          'This task rule will be used for any asset whose type is not covered by other task rules in this template.'
        )}
        placement={placement}
      >
        {content}
      </HoverTooltip>
    );
    // Preserve key by wrapping in a fragment if key is provided
    return key !== undefined ? (
      <React.Fragment key={key}>{tooltipContent}</React.Fragment>
    ) : (
      tooltipContent
    );
  }
  // Preserve key by wrapping in a fragment if key is provided
  return key !== undefined ? (
    <React.Fragment key={key}>{content}</React.Fragment>
  ) : (
    content
  );
};

const PopoverOpenObserver = ({
  open,
  onChange,
}: {
  open: boolean;
  onChange: (open: boolean) => void;
}) => {
  useEffect(() => {
    onChange(open);
  }, [open, onChange]);
  return null;
};

export function AssetTypeSelect({ value, onChange, options, error }: Props) {
  const { t } = useTranslation();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [query, setQuery] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);

  // Transform options to match Combolist's Identifiable interface
  const combolistItems = useMemo(() => {
    return options.map((option) => ({
      id: option.value,
      name: option.label,
      $group: option.$group,
    }));
  }, [options]);

  // Create a Map for O(1) option lookups
  const optionsMap = useMemo(() => {
    return new Map(options.map((opt) => [opt.value, opt]));
  }, [options]);

  // Transform selected values to match Combolist's expected format
  const selectedItems = useMemo(() => {
    return value.map((selectedValue) => {
      const option = optionsMap.get(selectedValue);
      return {
        id: selectedValue,
        name: option?.label || selectedValue,
        $group: option?.$group,
      };
    });
  }, [value, optionsMap]);

  // Handle Combolist onChange by extracting the values
  const handleCombolistChange = useCallback(
    (selectedIdentifiables: CombolistItem[]) => {
      const selectedValues = selectedIdentifiables.map((item) => item.id);
      onChange(selectedValues);
    },
    [onChange]
  );

  // Scroll into view when popover opens
  useEffect(() => {
    if (isPopoverOpen && containerRef.current) {
      // Small delay to ensure popover is rendered
      const timeoutId = setTimeout(() => {
        containerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }, 100);

      // Cleanup timeout if component unmounts or effect re-runs
      return () => clearTimeout(timeoutId);
    }
  }, [isPopoverOpen]);

  const handleRemove = (assetType: string) => {
    const newValue = value.filter((id) => id !== assetType);
    onChange(newValue);
  };

  const handleClosed = () => {
    if (cancelling) {
      setCancelling(false);
      return;
    }
  };

  return (
    <>
      <Label>{t('assetType')}</Label>
      <Popover className='relative' ref={containerRef}>
        <Popover.Button
          className={classNames(
            'input relative mt-0.5 w-full overflow-hidden rounded border border-grey-20 bg-white pl-2 pr-3 focus-visible:border-grey-40 focus-visible:outline-none',
            selectedItems.length === 0 ? 'h-[46px]' : 'py-1'
          )}
          data-selected-count={selectedItems.length}
        >
          <div className='flex items-center'>
            <div
              className='flex flex-1 flex-wrap gap-1'
              data-asset-type-selections
            >
              {value.slice(0, DISPLAY_LIMIT).map((selectedValue) => {
                const option = optionsMap.get(selectedValue);
                const content = (
                  <span className='flex max-w-full items-center gap-2 rounded-md bg-hoverBg px-2.5 py-1.5'>
                    {option?.label || selectedValue}
                    <button
                      type='button'
                      className='text-brand-200 hover:bg-brand-200 hover:text-brand-900 focus:bg-brand-500 focus:ring-brand-500 focus:ring-offset-brand-50 ml-1 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full focus:text-white focus:outline-none focus:ring-2 focus:ring-offset-2'
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(selectedValue);
                      }}
                    >
                      <span className='sr-only'>
                        Remove {option?.label || selectedValue}
                      </span>
                      <svg
                        className='h-2 w-2'
                        stroke='currentColor'
                        fill='none'
                        viewBox='0 0 8 8'
                      >
                        <path
                          strokeLinecap='round'
                          strokeWidth='1.5'
                          d='m1 1 6 6m0-6L1 7'
                        />
                      </svg>
                    </button>
                  </span>
                );

                return wrapWithOtherAssetTypeTooltip(
                  selectedValue,
                  content,
                  'top',
                  t,
                  selectedValue
                );
              })}
              {value.length > DISPLAY_LIMIT && (
                <span className='flex max-w-full items-center gap-2 rounded-md bg-grey-5 px-2.5 py-1.5'>
                  <span className='truncate text-grey-50'>
                    +{value.length - DISPLAY_LIMIT} more
                  </span>
                </span>
              )}
            </div>
            <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
              <svg
                className='h-5 w-5 text-gray-400'
                viewBox='0 0 20 20'
                fill='currentColor'
                aria-hidden='true'
              >
                <path
                  fillRule='evenodd'
                  d='M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
          </div>
        </Popover.Button>
        {error && <p className='ErrorMessage'>{error}</p>}
        <MenuTransition>
          <PopoverPanel className='absolute top-full z-50 mt-0.5 w-full rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'>
            {({ open }) => {
              return (
                <>
                  <PopoverOpenObserver
                    open={open}
                    onChange={(nextOpen) => {
                      // Only update local state when the popover's open state actually changes.
                      // This prevents unnecessary re-renders while the popover remains open/closed.
                      setIsPopoverOpen((prev) =>
                        prev === nextOpen ? prev : nextOpen
                      );
                    }}
                  />
                  <PopoverStateChangedEffect
                    open={open}
                    onClose={handleClosed}
                  />

                  {/* Sticky Filter Bar - Following SiteSelect pattern */}
                  <div className='sticky -top-1 z-50 -mt-1 mb-1 bg-white p-1 pb-0'>
                    <SearchInput
                      className='rounded-full border-none bg-grey-3'
                      inputProps={{ className: 'bg-grey-3' }}
                      placeholder={t('asset_plural')}
                      setQuery={setQuery}
                      query={query}
                    />
                  </div>

                  {/* Asset List */}
                  <div className='max-h-64 overflow-y-auto'>
                    <Combolist
                      items={combolistItems}
                      searchKeys={['name']}
                      placeholder={t('asset_plural')}
                      value={selectedItems}
                      onChange={handleCombolistChange}
                      multiple={true}
                      searchable={false} // We handle search above
                      renderInner={(item) => {
                        const content = (
                          <div className='flex min-w-0 flex-col'>
                            <span className='truncate text-base'>
                              {item.name}
                            </span>
                          </div>
                        );

                        return wrapWithOtherAssetTypeTooltip(
                          item.id,
                          content,
                          'right',
                          t
                        );
                      }}
                    />
                  </div>

                  <div className='z-10 flex border-t bg-white p-1.5 text-xs'>
                    <Popover.Button
                      className='flex-1 p-2 text-left uppercase'
                      onClick={() => setCancelling(true)}
                    >
                      {t('cancel')}
                    </Popover.Button>
                    <Popover.Button className='flex-1 p-2 text-right uppercase'>
                      {t('ok')}
                    </Popover.Button>
                  </div>
                </>
              );
            }}
          </PopoverPanel>
        </MenuTransition>
      </Popover>
    </>
  );
}
